import { CognitoUser } from '@aws-amplify/auth';
import { CognitoUserSession } from 'amazon-cognito-identity-js';

import { CHALLENGE_NAMES } from '@/lib/models';

export enum COGNITO_CHALLENGE_FLOW {
	COMPLETE = 'COMPLETE',
	MFA_SETUP = 'MFA_SETUP',
	MFA_CODE = 'MFA_CODE',
	NEW_PASSWORD = 'NEW_PASSWORD',
}

export function getCognitoChallengeFlow(challengeName?: string) {
	switch (challengeName) {
		case CHALLENGE_NAMES.SELECT_MFA_TYPE:
		case CHALLENGE_NAMES.MFA_SETUP:
			return COGNITO_CHALLENGE_FLOW.MFA_SETUP;
		case CHALLENGE_NAMES.SMS_MFA:
		case CHALLENGE_NAMES.SOFTWARE_TOKEN_MFA:
			return COGNITO_CHALLENGE_FLOW.MFA_CODE;
		case CHALLENGE_NAMES.NEW_PASSWORD_REQUIRED:
			return COGNITO_CHALLENGE_FLOW.NEW_PASSWORD;
		case CHALLENGE_NAMES.CUSTOM_CHALLENGE:
		default:
			return COGNITO_CHALLENGE_FLOW.COMPLETE;
	}
}

export function refreshCognitoUserSession(cognitoUser: CognitoUser) {
	return new Promise((resolve: (session: CognitoUserSession) => void, reject: (error: Error) => void) => {
		cognitoUser.getSession(async (sessionError: Error | null, session: CognitoUserSession | null) => {
			if (sessionError) {
				reject(sessionError);
				return;
			}

			if (!session) {
				reject(new Error('Error getting CognitoUser session.'));
				return;
			}

			cognitoUser.refreshSession(
				session.getRefreshToken(),
				async (refreshError: Error | null, session: CognitoUserSession | null) => {
					if (refreshError) {
						reject(refreshError);
						return;
					}

					if (session) {
						resolve(session);
						return;
					}

					reject(new Error('Error refreshing CognitoUser token.'));
					return;
				}
			);
		});
	});
}

/**
 * Device key Amplify confirmed for this user on a previous sign-in, loaded from its local cache.
 * A refresh token issued by a user pool with device tracking is bound to that key, so a platform
 * that receives the tokens without it cannot refresh them. Undefined when no device was confirmed.
 */
export function getCachedDeviceKey(cognitoUser: CognitoUser): string | undefined {
	cognitoUser.getCachedDeviceKeyAndPassword();
	return (cognitoUser as unknown as { deviceKey?: string }).deviceKey || undefined;
}
